import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API, formatINR, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Upload, Download, X, ImagePlus, Crop } from "lucide-react";
import ImageCropModal from "@/components/ImageCropModal";
import Portal from "@/components/Portal";

const EMPTY = {
  slug: "", name: "", category: "beds", sub_category: "",
  starts_at: "", compare_at: "", material: "", color: "", wood_finish: "",
  images: [], description: "", customization: {}, featured: false, new_arrival: false,
};

const slugify = (s) =>
  (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

async function uploadOne(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`${API}/admin/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
}

function ImageUploader({ images, onChange }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(0);
  const [editIdx, setEditIdx] = useState(null);
  const [editUrl, setEditUrl] = useState(null);
  const inputRef = useRef();

  const upload = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setBusy(files.length);
    const results = [];
    for (const f of files) {
      try {
        const url = await uploadOne(f);
        results.push(url);
      } catch (e) {
        toast.error(`${f.name}: ${formatApiErrorDetail(e.response?.data?.detail) || "upload failed"}`);
      } finally {
        setBusy((b) => b - 1);
      }
    }
    if (results.length) {
      onChange([...(images || []), ...results]);
      toast.success(`${results.length} image${results.length > 1 ? "s" : ""} uploaded`);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    upload(e.dataTransfer.files);
  };

  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const to = i + dir;
    if (to < 0 || to >= images.length) return;
    const next = [...images];
    [next[i], next[to]] = [next[to], next[i]];
    onChange(next);
  };

  const isEditable = (url) => {
    if (!url) return false;
    // Server-side crop works for any URL the backend can fetch:
    // - our own /api/files/ storage
    // - external CORS-enabled hosts (unsplash etc)
    return true;
  };

  const openEdit = (i) => {
    setEditIdx(i);
    setEditUrl(images[i]);
  };

  const onCropDone = (newUrl) => {
    const idx = editIdx;
    setEditIdx(null);
    setEditUrl(null);
    onChange(images.map((im, i) => (i === idx ? newUrl : im)));
  };

  return (
    <div data-testid="admin-image-uploader">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className="p-8 flex flex-col items-center justify-center cursor-pointer transition-colors duration-500 text-center"
        style={{ border: `1px dashed ${drag ? "#B8895C" : "#2A2825"}`, background: "#0B0B0C" }}
        data-testid="admin-image-dropzone"
      >
        <ImagePlus size={28} strokeWidth={1} className="text-[#B8895C] mb-3" />
        <div className="font-serif text-[#EDE4D3] text-lg">
          {busy > 0 ? `Uploading ${busy} image${busy > 1 ? "s" : ""}…` : "Drop images here or click to upload"}
        </div>
        <div className="font-micro text-[#8A8680] mt-2">JPG · PNG · WEBP — multi-file supported · Crop after upload</div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
          data-testid="admin-image-file-input"
        />
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-3 mt-4" data-testid="admin-product-images">
          {images.map((im, i) => (
            <div key={i} className="relative aspect-[4/5] group" style={{ background: "#0B0B0C", border: "1px solid var(--hairline)" }}>
              <img src={im} alt="" className="w-full h-full object-cover" />
              {i === 0 && (
                <div className="absolute top-1 left-1 px-2 py-0.5 bg-[#B8895C] text-[#0B0B0C] font-micro text-[9px]">
                  Cover
                </div>
              )}
              <div className="absolute top-1 right-1 flex gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); openEdit(i); }}
                  title="Crop &amp; adjust"
                  className="px-2 h-6 bg-[#B8895C] text-[#0B0B0C] flex items-center justify-center gap-1 font-micro text-[9px] hover:opacity-80"
                  data-testid={`admin-image-edit-${i}`}
                >
                  <Crop size={10} strokeWidth={2} /> CROP
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(i); }}
                  title="Remove"
                  className="w-6 h-6 bg-[#0B0B0C]/90 text-[#EDE4D3] flex items-center justify-center hover:text-[#B8895C]"
                  data-testid={`admin-image-remove-${i}`}
                >
                  <X size={12} />
                </button>
              </div>
              <div className="absolute bottom-1 left-1 right-1 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <button
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="w-6 h-6 bg-[#0B0B0C]/90 text-[#EDE4D3] font-micro text-xs disabled:opacity-30"
                  data-testid={`admin-image-move-left-${i}`}
                >‹</button>
                <button
                  onClick={() => move(i, 1)}
                  disabled={i === images.length - 1}
                  className="w-6 h-6 bg-[#0B0B0C]/90 text-[#EDE4D3] font-micro text-xs disabled:opacity-30"
                  data-testid={`admin-image-move-right-${i}`}
                >›</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editUrl && (
        <ImageCropModal
          imageUrl={editUrl}
          onClose={() => { setEditUrl(null); setEditIdx(null); }}
          onConfirm={onCropDone}
        />
      )}
    </div>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [slugTouched, setSlugTouched] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const bulkRef = useRef();
  const scrollRef = useRef(0);

  const bulkNormalize = async () => {
    if (!window.confirm("Crop every product image to 4:5 aspect ratio? This may take a minute.")) return;
    setNormalizing(true);
    try {
      const { data } = await axios.post(`${API}/admin/products/normalize-images`);
      toast.success(`${data.processed_images} images cropped across ${data.products_updated} products`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Normalize failed");
    } finally {
      setNormalizing(false);
    }
  };

  const load = async () => {
    const [{ data: p }, { data: c }] = await Promise.all([
      axios.get(`${API}/products?limit=500`),
      axios.get(`${API}/categories`),
    ]);
    setProducts(p);
    setCategories(c);
  };
  useEffect(() => { load(); }, []);

  // Lock body scroll while the editor modal is open. Browsers preserve
  // window.scrollY across `overflow:hidden` toggles, so no manual restore
  // is needed — the page stays exactly where the user clicked from.
  useEffect(() => {
    if (!editing) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [editing]);

  const filtered = filterCat === "all" ? products : products.filter((p) => p.category === filterCat);

  const openNew = () => {
    scrollRef.current = window.scrollY;
    setSlugTouched(false);
    setEditing({ ...EMPTY });
  };

  const openEdit = (p) => {
    scrollRef.current = window.scrollY;
    setSlugTouched(true);
    setEditing({ ...p, images: p.images || [] });
  };

  const setField = (k, v) => setEditing((prev) => ({ ...prev, [k]: v }));
  const setName = (v) => {
    setEditing((prev) => ({
      ...prev,
      name: v,
      slug: slugTouched ? prev.slug : slugify(v),
    }));
  };

  const save = async () => {
    if (!editing.name?.trim()) return toast.error("Please enter a product name");
    if (!Number(editing.starts_at)) return toast.error("Please enter a starting price");
    setSaving(true);
    try {
      const payload = {
        slug: (editing.slug || "").trim(),
        name: editing.name.trim(),
        category: editing.category,
        sub_category: editing.sub_category || "",
        starts_at: Number(editing.starts_at),
        compare_at: editing.compare_at ? Number(editing.compare_at) : null,
        material: editing.material || "",
        color: editing.color || "",
        wood_finish: editing.wood_finish || "",
        images: Array.isArray(editing.images) ? editing.images : [],
        description: editing.description || "",
        customization: editing.customization || {},
        featured: !!editing.featured,
        new_arrival: !!editing.new_arrival,
      };
      if (editing.id) {
        await axios.put(`${API}/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload);
        toast.success("Product created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await axios.delete(`${API}/admin/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const bulkImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await axios.post(`${API}/admin/products/bulk-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} inserted · ${data.updated} updated · ${data.failed} failed`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk import failed");
    } finally {
      e.target.value = "";
    }
  };

  const bulkExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/products/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `products-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported to CSV");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="fade-in" data-testid="admin-products-page">
      <div className="flex justify-between items-end mb-10 flex-wrap gap-4">
        <div>
          <div className="font-micro text-[#8A8680] mb-3">Catalog</div>
          <h1 className="font-serif text-[#EDE4D3] text-5xl">Products.</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button onClick={bulkExport} className="btn-ghost" data-testid="admin-product-export"><Download size={14} className="inline mr-2" /> Export CSV</button>
          <button onClick={bulkNormalize} disabled={normalizing} className="btn-ghost" data-testid="admin-product-normalize">
            {normalizing ? "Cropping…" : "Auto-crop All to 4:5"}
          </button>
          <button onClick={() => bulkRef.current?.click()} className="btn-ghost" data-testid="admin-product-import"><Upload size={14} className="inline mr-2" /> Bulk Import CSV</button>
          <input ref={bulkRef} type="file" accept=".csv,text/csv" className="hidden" onChange={bulkImport} data-testid="admin-product-import-input" />
          <button className="btn-bronze" onClick={openNew} data-testid="admin-product-new">
            <Plus size={14} className="inline mr-2" /> New Product
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-testid="admin-product-filters">
        <button className={`chip ${filterCat === "all" ? "active" : ""}`} onClick={() => setFilterCat("all")}>All ({products.length})</button>
        {categories.map((c) => (
          <button key={c.slug} className={`chip ${filterCat === c.slug ? "active" : ""}`} onClick={() => setFilterCat(c.slug)} data-testid={`admin-filter-${c.slug}`}>
            {c.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="admin-products-grid">
        {filtered.map((p) => (
          <div key={p.id} className="group" style={{ background: "#141414", border: "1px solid var(--hairline)" }} data-testid={`admin-product-${p.slug}`}>
            <div className="aspect-[4/5] overflow-hidden" style={{ background: p.swatch_hex || "#0B0B0C" }}>
              {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="p-5">
              <div className="font-serif text-[#EDE4D3] text-lg">{p.name}</div>
              <div className="font-micro text-[#8A8680] mt-1">{p.category} · {p.sub_category}</div>
              <div className="font-serif text-[#B8895C] mt-3">{formatINR(p.starts_at)}</div>
              <div className="flex gap-3 mt-4">
                <button onClick={() => openEdit(p)} className="font-micro text-[#EDE4D3] link-underline">Edit</button>
                <button onClick={() => del(p)} className="font-micro text-[#8A8680] hover:text-[#B8895C]"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Portal>
          <div
            className="fixed inset-0 z-50 bg-black/80 overflow-y-auto"
            data-testid="admin-product-editor-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}
          >
            <div
              className="w-full max-w-3xl mx-auto my-6 md:my-12 p-8 md:p-10"
              style={{ background: "#141414", border: "1px solid var(--hairline)" }}
              data-testid="admin-product-editor"
            >
            <div className="flex justify-between items-center mb-8">
              <div>
                <div className="font-micro text-[#8A8680] mb-1">{editing.id ? "Editing" : "New"}</div>
                <h2 className="font-serif text-[#EDE4D3] text-3xl">{editing.id ? "Edit Product" : "Add a Product"}</h2>
              </div>
              <button onClick={() => setEditing(null)} className="font-micro text-[#8A8680] link-underline" data-testid="admin-product-cancel">Cancel</button>
            </div>

            <div className="space-y-6">
              <div>
                <div className="font-micro text-[#8A8680] mb-3">Basics</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <input
                    className="input-teak"
                    placeholder="Product name *"
                    value={editing.name || ""}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="admin-product-name"
                  />
                  <input
                    className="input-teak"
                    placeholder="URL slug (auto)"
                    value={editing.slug || ""}
                    onChange={(e) => { setSlugTouched(true); setField("slug", slugify(e.target.value)); }}
                    data-testid="admin-product-slug"
                  />
                  <select className="input-teak" value={editing.category} onChange={(e) => setField("category", e.target.value)} data-testid="admin-product-category">
                    {categories.map((c) => <option key={c.slug} value={c.slug} style={{ background: "#141414" }}>{c.name}</option>)}
                  </select>
                  <input className="input-teak" placeholder="Sub-category (e.g. King)" value={editing.sub_category || ""} onChange={(e) => setField("sub_category", e.target.value)} data-testid="admin-product-subcat" />
                </div>
              </div>

              <div>
                <div className="font-micro text-[#8A8680] mb-3">Pricing (INR)</div>
                <div className="grid grid-cols-2 gap-5">
                  <input className="input-teak" placeholder="Starts at *" type="number" value={editing.starts_at} onChange={(e) => setField("starts_at", e.target.value)} data-testid="admin-product-price" />
                  <input className="input-teak" placeholder="Compare-at (crossed price)" type="number" value={editing.compare_at || ""} onChange={(e) => setField("compare_at", e.target.value)} />
                </div>
              </div>

              <div>
                <div className="font-micro text-[#8A8680] mb-3">Attributes</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <input className="input-teak" placeholder="Material" value={editing.material || ""} onChange={(e) => setField("material", e.target.value)} />
                  <input className="input-teak" placeholder="Color" value={editing.color || ""} onChange={(e) => setField("color", e.target.value)} />
                  <input className="input-teak" placeholder="Wood finish" value={editing.wood_finish || ""} onChange={(e) => setField("wood_finish", e.target.value)} />
                </div>
              </div>

              <div>
                <div className="font-micro text-[#8A8680] mb-3">Images (first image is the cover)</div>
                <ImageUploader images={editing.images || []} onChange={(imgs) => setField("images", imgs)} />
              </div>

              <div>
                <div className="font-micro text-[#8A8680] mb-3">Description</div>
                <textarea
                  className="input-teak"
                  rows={4}
                  placeholder="Describe the piece — materials, craft, dimensions."
                  value={editing.description || ""}
                  onChange={(e) => setField("description", e.target.value)}
                />
              </div>

              <div className="flex gap-8 pt-2">
                <label className="font-micro text-[#D8D6D1] flex gap-2 items-center cursor-pointer">
                  <input type="checkbox" checked={!!editing.featured} onChange={(e) => setField("featured", e.target.checked)} /> Featured
                </label>
                <label className="font-micro text-[#D8D6D1] flex gap-2 items-center cursor-pointer">
                  <input type="checkbox" checked={!!editing.new_arrival} onChange={(e) => setField("new_arrival", e.target.checked)} /> New Arrival
                </label>
              </div>
            </div>

            <div className="mt-10 flex gap-3">
              <button className="btn-bronze flex-1" onClick={save} disabled={saving} data-testid="admin-product-save">
                {saving ? "Saving…" : editing.id ? "Save Changes" : "Create Product"}
              </button>
              <button className="btn-ghost" onClick={() => setEditing(null)} data-testid="admin-product-cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
}
